import { makeAutoObservable } from 'mobx';
import jwt_decode from 'jwt-decode';
import { callApi, logoutGoogle } from '../util/apiWrapper';
import { msalConfig } from '../services/msal';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { appInsights } from '../services/appInsights';
import { DateTime } from 'luxon';

const IMPERSONATION_STORAGE_KEY = 'AuthStoreImpersonation';

class AuthStore {
  authToken = '';
  notAuthorized = false;
  user = null;
  loading = false;
  authExpires = DateTime.now().plus({ hours: 10 });
  msalInstance;
  maslAccount;
  roles = [];
  isImpersonating = false;
  adminUser = null;
  impersonatedUser = null;
  impersonation = null;

  constructor(msalInstance) {
    this.msalInstance = msalInstance;
    makeAutoObservable(this, {}, { autoBind: true });
    this.loadImpersonationState();
  }

  get getUser() {
    return this.user;
  }

  get getToken() {
    return this.authToken;
  }

  get getAuthExpires() {
    return this.authExpires;
  }

  setUser = (user) => {
    this.user = user;
  };

  setAuthToken = (token) => {
    this.authToken = token;
  };

  setAuthExpires = () => {
    this.authExpires = DateTime.now().plus({ hours: 10 });
  };

  setLoading = (val) => {
    this.loading = val;
  };

  setNotAuthorized(isAuthorized) {
    this.notAuthorized = isAuthorized;
    this.user = null;
    this.authToken = '';
    this.roles = [];
    this.clearImpersonationState();
  }

  setRoles = (roles) => {
    this.roles = roles;
  };

  logoutUser() {
    this.notAuthorized = false;
    this.user = null;
    this.authToken = '';
    this.roles = [];
    this.clearImpersonationState();
  }

  normalizeImpersonationUser(user) {
    if (!user) {
      return null;
    }
    return {
      ...user,
      id: user.id ?? user.Id,
    };
  }

  setImpersonationState({ adminUser, impersonatedUser, impersonation }) {
    this.adminUser = adminUser;
    this.impersonatedUser = impersonatedUser;
    this.impersonation = impersonation;
    this.isImpersonating = true;
    this.saveImpersonationState();
  }

  clearImpersonationState() {
    this.isImpersonating = false;
    this.adminUser = null;
    this.impersonatedUser = null;
    this.impersonation = null;
    this.clearImpersonationStateStorage();
  }

  saveImpersonationState() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    const state = {
      isImpersonating: this.isImpersonating,
      adminUser: this.adminUser,
      impersonatedUser: this.impersonatedUser,
      impersonation: this.impersonation,
    };
    window.sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(state));
  }

  clearImpersonationStateStorage() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  }

  loadImpersonationState() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    const savedState = window.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!savedState) {
      return;
    }

    try {
      const parsedState = JSON.parse(savedState);
      const impersonationEnd = parsedState?.impersonation?.impersonation_end;
      const hasExpiredImpersonation = impersonationEnd ? DateTime.fromISO(impersonationEnd) <= DateTime.now() : false;

      if (!parsedState?.isImpersonating || !parsedState?.impersonatedUser || hasExpiredImpersonation) {
        this.clearImpersonationState();
        return;
      }

      this.isImpersonating = true;
      this.adminUser = parsedState.adminUser ?? null;
      this.impersonatedUser = parsedState.impersonatedUser;
      this.impersonation = parsedState.impersonation ?? null;
      this.setUser({ result: parsedState.impersonatedUser });
      this.setRoles(parsedState.impersonatedUser.roles || []);
      this.notAuthorized = false;
    } catch (error) {
      this.clearImpersonationState();
      console.warn('Unable to load impersonation session state:', error);
    }
  }

  handleImpersonatedLogin(response) {
    const impersonationResponse = response?.Result ?? response?.result;
    const adminUser = this.normalizeImpersonationUser(impersonationResponse?.admin_user);
    const impersonatedUser = this.normalizeImpersonationUser(impersonationResponse?.impersonated_user);

    if (!impersonatedUser) {
      throw new Error('Unable to start impersonation: impersonated user details were not returned.');
    }

    this.setImpersonationState({
      adminUser,
      impersonatedUser,
      impersonation: {
        is_active: impersonationResponse?.is_active,
        created_at: impersonationResponse?.created_at,
        impersonation_end: impersonationResponse?.impersonation_end,
      },
    });
    this.setUser({ result: impersonatedUser });
    this.setRoles(impersonatedUser.roles || []);
    this.notAuthorized = false;

    appInsights.trackEvent({
      name: 'impersonation_start',
      properties: {
        admin_email: adminUser?.email,
        impersonated_email: impersonatedUser?.email,
      },
    });
  }

  handleImpersonationEnd(response) {
    const impersonationResponse = response?.Result ?? response?.result;
    const responseAdminUser = this.normalizeImpersonationUser(impersonationResponse?.admin_user);
    const adminUser = responseAdminUser || this.adminUser;

    if (adminUser) {
      this.setUser({ result: adminUser });
      this.setRoles(adminUser.roles || []);
      this.notAuthorized = false;
    } else {
      this.setUser(null);
      this.setRoles([]);
    }

    this.clearImpersonationState();
    appInsights.trackEvent({
      name: 'impersonation_end',
      properties: {
        admin_email: adminUser?.email,
      },
    });
  }

  // NOTE: There is never a guarantee that a token can be acquired silently even if the refresh token has not expired yet. The
  // patterns described above are best effort attempts to minimize interaction at inconvenient times but will not eliminate
  // the possibility of required interactions within the desired timeframes.

  async handleUserLogin(account) {
    try {
      this.setLoading(true);
      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes: msalConfig.api.scopes,
        account: account,
        forceRefresh: true,
        refreshTokenExpirationOffsetSeconds: 36000,
      });
      this.setAuthToken(tokenResponse.accessToken);
      this.setAuthExpires();
      this.maslAccount = account;
      this.notAuthorized = false;
      if (this.isImpersonating) {
        this.setLoading(false);
        return;
      }
      appInsights.trackEvent({ name: 'login', properties: { email: account.idTokenClaims.emails[0] } });
      const userResponse = await callApi(this, `user?email=${account.idTokenClaims.emails[0]}&is_active=1&is_login=1`, 'GET', null);
      this.setUser(userResponse);
      this.setRoles(userResponse.result.roles);
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      this.setNotAuthorized(true);
      if (JSON.stringify(error).indexOf('InteractionRequiredAuthError') > -1) {
        await logoutGoogle();
        this.msalInstance.loginRedirect({ ...msalConfig.loginRequest, redirectStartPage: '/' });
      } else {
        appInsights.trackException({ error: error, severityLevel: SeverityLevel.Error });
        console.log('error login', error);
      }
    }
  }

  async checkToken() {
    try {
      if (!this.user) {
        // this.msalInstance.loginRedirect(msalConfig.loginRequest);
        return;
      }
      if (!this.maslAccount) {
        // this.msalInstance.loginRedirect(msalConfig.loginRequest);
        return;
      }
      if (this.authExpires > DateTime.now()) {
        if (this.isTokenCloseToExpire(this.user.accessToken)) {
          this.setLoading(true);
          const tokenResponse = await this.msalInstance.acquireTokenSilent({
            scopes: msalConfig.api.scopes,
            account: this.maslAccount,
          });
          this.user.accessToken = tokenResponse.accessToken;
          this.setAuthToken(tokenResponse.accessToken);
          this.setLoading(false);
        }
        return this.accessToken;
      } else {
        this.logoutUser();
        await this.msalInstance.logoutRedirect();
      }
    } catch (error) {
      this.setLoading(false);
      console.log('error login', error);
      throw error;
    }
  }

  isTokenCloseToExpire = (token) => {
    try {
      const decoded = jwt_decode(token);
      const expDate = new Date(decoded.exp * 1000);
      const now = new Date();
      expDate.setMinutes(expDate.getMinutes() - 10);
      if (expDate.getTime() < now.getTime()) {
        return true;
      } else return false;
    } catch (err) {
      console.log(err);
      return true;
    }
  };

  isTokenExpired = () => {
    try {
      const decoded = jwt_decode(this.authToken);
      const expDate = new Date(decoded.exp * 1000);
      const now = new Date();
      if (expDate.getTime() < now.getTime()) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.log(err);
      return true;
    }
  };
}

export default AuthStore;
